// Archivo: ui/StockEntryForm.js
import React, { useState, useMemo, useEffect } from 'react';
import { Combobox } from '@headlessui/react';

export default function StockEntryForm({ products, designs, locations, onSave, onCancel, initialData = null }) {
    const [productId, setProductId] = useState('');
    const [locationId, setLocationId] = useState('');
    const [size, setSize] = useState('');
    const [color, setColor] = useState('');
    const [designId, setDesignId] = useState('NONE');
    const [designSize, setDesignSize] = useState('');
    const [quantity, setQuantity] = useState('');
    const [error, setError] = useState('');
    const [productQuery, setProductQuery] = useState('');
    const [designQuery, setDesignQuery] = useState('');

    useEffect(() => {
        if (initialData) {
            setProductId(initialData.productId || '');
            setLocationId(initialData.locationId || '');
            setSize(initialData.size || '');
            setColor(initialData.color || '');
            setDesignId(initialData.designId || 'NONE');
            setDesignSize(initialData.designSize || '');
            setQuantity(initialData.quantity || '');
        } else {
            setProductId('');
            setLocationId('');
            setSize('');
            setColor('');
            setDesignId('NONE');
            setDesignSize('');
            setQuantity('');
            setError('');
        }
    }, [initialData]); 

    const filteredProducts =
        productQuery === ''
            ? products
            : products.filter((product) =>
                product.name
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(productQuery.toLowerCase().replace(/\s+/g, ''))
            );

    const filteredDesigns =
        designQuery === ''
            ? designs
            : designs.filter((design) =>
                design.name
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(designQuery.toLowerCase().replace(/\s+/g, ''))
            );

    const selectedProduct = useMemo(() => {
        return products.find(p => p.id === productId);
    }, [productId, products]);

    const selectedDesign = useMemo(() => {
        if (designId === 'NONE') return null;
        return designs.find(d => d.id === designId);
    }, [designId, designs]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        if (!productId || !locationId || !size || !color || !quantity) {
            setError('Todos los campos son obligatorios, excepto el diseño.');
            return;
        }
        if (designId !== 'NONE' && !designSize) {
            setError('Si seleccionas un diseño, también debes seleccionar su talla.');
            return;
        }
        const formData = {
            productId,
            locationId,
            size,
            color,
            designId: designId === 'NONE' ? 'NONE' : designId,
            designSize: designId === 'NONE' ? null : designSize,
            quantity: parseInt(quantity, 10) || 0,
        };
        onSave(formData);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        if (selectedDesign && selectedDesign.sizes?.includes(newSize)) {
            setDesignSize(newSize);
        }
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <Combobox 
                    value={productId} 
                    onChange={(newProductId) => {
                        setProductId(newProductId);
                        setSize('');
                        setColor('');
                    }}
                >
                    <Combobox.Label className="block text-sm font-medium text-gray-700">Producto</Combobox.Label>
                    <div className="relative mt-1">
                        <Combobox.Input
                            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 sm:text-sm"
                            onChange={(event) => setProductQuery(event.target.value)}
                            displayValue={(pId) => products.find((p) => p.id === pId)?.name || ''}
                            placeholder="-- Selecciona o busca un producto --"
                            onClick={(event) => event.target.select()}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a.75.75 0 01.53.22l3.5 3.5a.75.75 0 01-1.06 1.06L10 4.81 7.03 7.78a.75.75 0 01-1.06-1.06l3.5-3.5A.75.75 0 0110 3zm-3.78 9.28a.75.75 0 011.06 0L10 15.19l2.97-2.91a.75.75 0 111.06 1.06l-3.5 3.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 010-1.06z" clipRule="evenodd" />
                            </svg>
                        </Combobox.Button>
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {filteredProducts.map((product) => (
                                <Combobox.Option
                                    key={product.id}
                                    value={product.id}
                                    className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${ active ? 'bg-cyan-600 text-white' : 'text-gray-900' }`}
                                >
                                    {({ selected }) => (
                                        <span className={`block truncate ${ selected ? 'font-semibold' : '' }`}>
                                            {product.name}
                                        </span>
                                    )}
                                </Combobox.Option>
                            ))}
                        </Combobox.Options>
                    </div>
                </Combobox>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="size" className="block text-sm font-medium text-gray-700">Talla</label>
                    <select 
                        id="size" 
                        value={size} 
                        onChange={e => handleSizeChange(e.target.value)}
                        disabled={!productId}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md disabled:bg-gray-100"
                    >
                        <option value="" disabled>-- Selecciona una talla --</option>
                        {selectedProduct?.sizes?.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="color" className="block text-sm font-medium text-gray-700">Color</label>
                     <select 
                        id="color" 
                        value={color} 
                        onChange={e => setColor(e.target.value)}
                        disabled={!productId}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md disabled:bg-gray-100"
                    >
                        <option value="" disabled>-- Selecciona un color --</option>
                        {selectedProduct?.colors?.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
            </div>
            
            <div>
                <Combobox 
                    value={designId} 
                    onChange={(newDesignId) => {
                        setDesignId(newDesignId);
                        setDesignSize('');
                    }}
                >
                    <Combobox.Label className="block text-sm font-medium text-gray-700">Diseño (Opcional)</Combobox.Label>
                    <div className="relative mt-1">
                        <Combobox.Input
                            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 sm:text-sm"
                            onChange={(event) => setDesignQuery(event.target.value)}
                            displayValue={(dId) => {
                                if (dId === 'NONE') return 'Sin Diseño (Liso)';
                                return designs.find((d) => d.id === dId)?.name || '';
                            }}
                            placeholder="-- Busca un diseño o elige 'Sin Diseño' --"
                            onClick={(event) => event.target.select()}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a.75.75 0 01.53.22l3.5 3.5a.75.75 0 01-1.06 1.06L10 4.81 7.03 7.78a.75.75 0 01-1.06-1.06l3.5-3.5A.75.75 0 0110 3zm-3.78 9.28a.75.75 0 011.06 0L10 15.19l2.97-2.91a.75.75 0 111.06 1.06l-3.5 3.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 010-1.06z" clipRule="evenodd" />
                            </svg>
                        </Combobox.Button>
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            <Combobox.Option key="NONE" value="NONE" className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${ active ? 'bg-cyan-600 text-white' : 'text-gray-900' }`}>
                                {({ selected }) => (<span className={`block truncate ${ selected ? 'font-semibold' : '' }`}>-- Sin Diseño (Liso) --</span>)}
                            </Combobox.Option>
                            {filteredDesigns.map((design) => (
                                <Combobox.Option key={design.id} value={design.id} className={({ active }) => `relative cursor-pointer select-none py-2 pl-3 pr-9 ${ active ? 'bg-cyan-600 text-white' : 'text-gray-900' }`}>
                                    {({ selected }) => (<span className={`block truncate ${ selected ? 'font-semibold' : '' }`}>{design.name}</span>)}
                                </Combobox.Option>
                            ))}
                        </Combobox.Options>
                    </div>
                </Combobox>
            </div>
            
            {selectedDesign && (
                <div>
                    <label htmlFor="designSize" className="block text-sm font-medium text-gray-700">Talla del Diseño</label>
                    <select id="designSize" value={designSize} onChange={e => setDesignSize(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md">
                        <option value="" disabled>-- Selecciona una talla --</option>
                        {selectedDesign.sizes?.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700">Ubicación de Ingreso</label>
                    <select id="location" value={locationId} onChange={e => setLocationId(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md">
                        <option value="" disabled>-- Selecciona una bodega --</option>
                        {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Cantidad</label>
                    <input type="number" id="quantity" value={quantity} onChange={e => setQuantity(e.target.value)} min="1" className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm" />
                </div>
            </div>

            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>}

            <div className="flex justify-end gap-4 pt-4">
                <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
                <button type="submit" className="btn-primary">
                    {initialData ? 'Actualizar Ítem' : 'Añadir al Lote'}
                </button>
            </div>
        </form>
    );
}